"use client";

import { createContext, type ReactNode, useContext } from "react";
import type { UserState } from "@/lib/entitlements";
import type { JobMatchTeaserData } from "@/lib/jobs/match-teaser";
import type { FitScoreBreakdown } from "@/lib/match/score";

// Lightweight per-user overlay context for the job detail page. Kept free of
// heavy client deps (better-auth, posthog) so consumer components and tests can
// import the hook/provider without pulling the whole island bundle. The
// fetching provider that populates it lives in `job-detail-client.tsx`.

export type JobContextValue = {
	loading: boolean;
	isAuthenticated: boolean;
	userState: UserState;
	canApply: boolean;
	isPreview: boolean;
	isAlreadyApplied: boolean;
	/**
	 * Whether this job is already in the user's tracker (any status) — the same
	 * signal the feed's heart icon uses. Resolved from the SAME
	 * `existingApplication` lookup `/api/me/job-context` already performs, so
	 * the detail page's save control costs no extra query.
	 *
	 * Optional for the same reason `matchTeaser` and `fitScoreBreakdown` are:
	 * existing `JobContextValue` literals (tests, other islands) must not have
	 * to be updated to add a field they do not read. Consumers default it.
	 */
	isSaved?: boolean;
	/** Server-resolved `hunt-coaching-surfaces` flag — gates the post-apply
	 * mock-interview coaching offer. Fails closed (false) until resolved. */
	coachingEnabled: boolean;
	/** Whether the signed-in user has a resume on file — personalizes the
	 * coaching copy, never gates it. */
	hasResume: boolean;
	/** Coaching offers already generated this calendar month — feeds the
	 * `decideCoachingMoment` frequency cap. */
	coachingSessionsThisMonth: number;
	/**
	 * `growth-jobdetail-match-teaser` payload — the real computed match score +
	 * salary hint for a signed-in user who has given Hunt enough preference
	 * signal. `undefined` while unresolved (guest, or not yet fetched), `null`
	 * once resolved for a signed-in user with too little signal to honestly
	 * personalize (see `PERSONALIZED_SIGNAL_THRESHOLD`). Optional so existing
	 * `JobContextValue` literals (tests, `guestJobContext`) don't need updating.
	 */
	matchTeaser?: JobMatchTeaserData | null;
	/**
	 * Fit-score rubric badge (W4) — the explainable breakdown (skill overlap,
	 * salary fit, seniority match, red flags) behind `matchTeaser.score`.
	 * `undefined` while unresolved (guest, or not yet fetched), `null` once
	 * resolved for a signed-in user with no Hunt signal source. Optional so
	 * existing `JobContextValue` literals (tests, `guestJobContext`) don't
	 * need updating.
	 */
	fitScoreBreakdown?: FitScoreBreakdown | null;
};

export function guestJobContext(guestIsPreview: boolean): JobContextValue {
	return {
		loading: false,
		isAuthenticated: false,
		userState: "guest",
		canApply: false,
		isPreview: guestIsPreview,
		isAlreadyApplied: false,
		isSaved: false,
		coachingEnabled: false,
		hasResume: false,
		coachingSessionsThisMonth: 0,
	};
}

const JobContext = createContext<JobContextValue | null>(null);

/**
 * Returns the per-user job overlay. Outside a provider (e.g. the shared
 * `AdSlotClient` on blog/feed pages) it falls back to the guest, non-preview
 * state so those call sites behave exactly as before.
 */
export function useJobContext(): JobContextValue {
	return useContext(JobContext) ?? guestJobContext(false);
}

/** Provides an explicit context value. Used by the fetching provider and tests. */
export function JobContextValueProvider({
	value,
	children,
}: {
	value: JobContextValue;
	children: ReactNode;
}) {
	return <JobContext.Provider value={value}>{children}</JobContext.Provider>;
}
